import { ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Save, Send, Loader2 } from "lucide-react";
import LoadingSpinner from "../LoadingSpinner";

interface AssessmentPageShellProps {
  title: string;
  subtitle?: string | undefined;
  backTo: string;
  backLabel?: string;
  isLoading?: boolean;
  error?: string | null;
  onSave?: () => void;
  onSubmit?: () => void;
  isSaving?: boolean;
  isSubmitting?: boolean;
  saveDisabled?: boolean;
  submitDisabled?: boolean;
  showActions?: boolean;
  children: ReactNode;
}

export default function AssessmentPageShell({
  title,
  subtitle,
  backTo,
  backLabel = "Back to Product",
  isLoading = false,
  error = null,
  onSave,
  onSubmit,
  isSaving = false,
  isSubmitting = false,
  saveDisabled = false,
  submitDisabled = false,
  showActions = true,
  children,
}: AssessmentPageShellProps) {
  const navigate = useNavigate();

  if (isLoading) {
    return <LoadingSpinner />;
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <p className="text-red-600 dark:text-red-400 mb-4">{error}</p>
          <button
            onClick={() => navigate(backTo)}
            className="btn btn-secondary"
          >
            {backLabel}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col">
      {/* Top bar */}
      <div className="flex items-center justify-between px-6 py-3 border-b border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 shrink-0">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate(backTo)}
            className="inline-flex items-center gap-1.5 text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100 transition-colors"
          >
            <ArrowLeft className="h-4 w-4" />
            {backLabel}
          </button>
          <div className="border-l border-gray-200 dark:border-gray-700 pl-4">
            <h1 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
              {title}
            </h1>
            {subtitle && (
              <p className="text-xs text-gray-500 dark:text-gray-400">
                {subtitle}
              </p>
            )}
          </div>
        </div>

        {showActions && (
          <div className="flex items-center gap-2">
            {onSave && (
              <button
                onClick={onSave}
                disabled={saveDisabled || isSaving}
                className="btn btn-secondary text-sm inline-flex items-center gap-1.5"
              >
                {isSaving ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Save className="h-4 w-4" />
                )}
                Save Draft
              </button>
            )}
            {onSubmit && (
              <button
                onClick={onSubmit}
                disabled={submitDisabled || isSubmitting}
                className="btn btn-primary text-sm inline-flex items-center gap-1.5"
              >
                {isSubmitting ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Send className="h-4 w-4" />
                )}
                Submit
              </button>
            )}
          </div>
        )}
      </div>

      {/* Content area */}
      <div className="flex-1 overflow-y-auto">{children}</div>
    </div>
  );
}
